/**
 * BookingsListPage - Admin list view of all bookings
 */

import { useState, useEffect, useRef } from 'react';
import { useDebounce } from '../../../hooks/useDebounce';
import { Link, useSearchParams } from 'react-router-dom';
import { formatTimeDisplay } from '@sda/shared';
import {
  getBookings,
  getAdminBookingClassRows,
  getShows,
  formatCurrency,
  type AdminBookingClassRow,
  type Booking,
  type Show,
} from '../../../api/admin';
import SortableHeader from '../../../components/admin/SortableHeader';
import Pagination from '../../../components/admin/Pagination';

type BookingStatus =
  | 'active' // virtual view: deposit_paid + confirmed + balance_paid
  | 'all'
  | 'pending'
  | 'deposit_paid'
  | 'confirmed'
  | 'balance_paid'
  | 'completed'
  | 'cancelled'
  | 'refunded';

const LIMIT = 50;
// CSV export walks the paginated endpoint; per_page is capped at 100 server-side.
const EXPORT_PAGE_SIZE = 100;
const EXPORT_MAX_PAGES = 50;
// The class view's endpoint allows bigger pages (up to 500), so a class-row export
// needs far fewer round trips. Same stop rule: an empty page or rows >= total.
const CLASS_EXPORT_PAGE_SIZE = 500;

// 'classes' (one row per class) is the default and stays out of the URL;
// 'bookings' is the grouped view, written as ?view=bookings.
type ListView = 'classes' | 'bookings';

const csvCell = (value: string) => `"${value.replace(/"/g, '""')}"`;

const FACULTY_CHIP_STYLES = {
  unassigned: 'bg-danger-100 text-danger-700',
  declined: 'bg-danger-100 text-danger-700',
  pending: 'bg-warning-100 text-warning-700',
  confirmed: 'bg-success-100 text-success-700',
} as const;

/** What the Faculty column says about one class. */
function classFacultyState(row: AdminBookingClassRow): {
  key: keyof typeof FACULTY_CHIP_STYLES;
  label: string;
} | null {
  // A cancelled class needs no faculty, so it gets no chip and no alarm.
  if (row.class.status === 'cancelled' || row.booking.status === 'cancelled') return null;
  if (!row.class.faculty) return { key: 'unassigned', label: 'Unassigned' };
  switch (row.class.faculty_assignment_status) {
    case 'accepted':
      return { key: 'confirmed', label: 'Confirmed' };
    case 'declined':
      return { key: 'declined', label: 'Declined' };
    default:
      return { key: 'pending', label: 'Pending' };
  }
}

const facultyName = (row: AdminBookingClassRow) =>
  row.class.faculty ? `${row.class.faculty.first_name} ${row.class.faculty.last_name}` : '';

export default function BookingsListPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bookings, setBookings] = useState<Booking[]>([]);
  const [classRows, setClassRows] = useState<AdminBookingClassRow[]>([]);
  const [view, setView] = useState<ListView>(searchParams.get('view') === 'bookings' ? 'bookings' : 'classes');
  // Which view the rows in state belong to, so a toggle never flashes the empty state.
  const [loadedView, setLoadedView] = useState<ListView | null>(null);
  // Grouped view: bookings whose full class list is expanded in place ("+N more").
  const [expandedBookingIds, setExpandedBookingIds] = useState<Set<string>>(new Set());
  const [shows, setShows] = useState<Show[]>([]);
  const [total, setTotal] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  // Set when the search matched too many studios for the backend to filter on at once,
  // so the list below is only a subset of the real matches.
  const [searchTruncated, setSearchTruncated] = useState(false);

  // Filters
  const [statusFilter, setStatusFilter] = useState<BookingStatus>(
    (searchParams.get('status') as BookingStatus) || 'active'
  );
  const [showFilter, setShowFilter] = useState(searchParams.get('show') || '');
  const [searchQuery, setSearchQuery] = useState(searchParams.get('q') || '');
  const [startDate, setStartDate] = useState(searchParams.get('start_date') || '');
  const [endDate, setEndDate] = useState(searchParams.get('end_date') || '');
  const [facultyFilter, setFacultyFilter] = useState<'all' | 'assigned' | 'unassigned'>(
    (searchParams.get('faculty') as 'all' | 'assigned' | 'unassigned') || 'all'
  );
  const [createdStart, setCreatedStart] = useState(searchParams.get('created_start') || '');
  const [createdEnd, setCreatedEnd] = useState(searchParams.get('created_end') || '');
  const tourStopFilter = searchParams.get('tour_stop') || '';
  const [page, setPage] = useState(1);

  // Sort — default: next upcoming class first (soonest class_date at top)
  const [sortBy, setSortBy] = useState('class_date');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');

  // Debounce the free-text search so we fetch once the user pauses typing,
  // not on every keystroke (prevents the list flickering blank per letter).
  const debouncedSearch = useDebounce(searchQuery, 300);
  // True once the first load has completed — used to show the full skeleton only
  // on initial load and keep the existing list visible during later refetches.
  const [hasLoaded, setHasLoaded] = useState(false);
  // Guards against out-of-order responses (latest request wins).
  const reqIdRef = useRef(0);

  useEffect(() => {
    loadShows();
  }, []);

  useEffect(() => {
    loadBookings();
    // Update URL params
    const params = new URLSearchParams();
    // 'active' is the default view — keep it out of the URL so the baseline stays clean.
    if (statusFilter !== 'active') params.set('status', statusFilter);
    if (showFilter) params.set('show', showFilter);
    if (debouncedSearch) params.set('q', debouncedSearch);
    if (startDate) params.set('start_date', startDate);
    if (endDate) params.set('end_date', endDate);
    if (facultyFilter !== 'all') params.set('faculty', facultyFilter);
    if (createdStart) params.set('created_start', createdStart);
    if (createdEnd) params.set('created_end', createdEnd);
    if (tourStopFilter) params.set('tour_stop', tourStopFilter);
    // 'classes' is the default view — keep it out of the URL, like the Active status.
    if (view !== 'classes') params.set('view', view);
    setSearchParams(params);
  }, [view, statusFilter, showFilter, debouncedSearch, startDate, endDate, facultyFilter, createdStart, createdEnd, tourStopFilter, page, sortBy, sortOrder]);

  const loadShows = async () => {
    try {
      const showsData = await getShows();
      setShows(showsData);
    } catch (err) {
      console.error('Failed to load shows:', err);
    }
  };

  const loadBookings = async () => {
    const reqId = ++reqIdRef.current;
    try {
      setIsLoading(true);
      setError(null);
      const listFilters = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        show_id: showFilter || undefined,
        tour_stop_id: tourStopFilter || undefined,
        search: debouncedSearch || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        faculty_assignment: facultyFilter !== 'all' ? facultyFilter : undefined,
        created_start: createdStart || undefined,
        created_end: createdEnd || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
        page,
        limit: LIMIT,
      };
      if (view === 'classes') {
        const { rows, total: totalCount, search_truncated } = await getAdminBookingClassRows(listFilters);
        // Ignore stale responses — only the latest request may update state.
        if (reqId !== reqIdRef.current) return;
        setClassRows(rows);
        setTotal(totalCount);
        setSearchTruncated(search_truncated === true);
      } else {
        const { bookings: bookingsData, total: totalCount, search_truncated } = await getBookings(listFilters);
        if (reqId !== reqIdRef.current) return;
        setBookings(bookingsData);
        setTotal(totalCount);
        setSearchTruncated(search_truncated === true);
      }
      setLoadedView(view);
    } catch (err) {
      if (reqId !== reqIdRef.current) return;
      setError(err instanceof Error ? err.message : 'Failed to load bookings');
      // A failed load still settles the view, so the empty state shows with the error.
      setLoadedView(view);
    } finally {
      if (reqId === reqIdRef.current) {
        setIsLoading(false);
        setHasLoaded(true);
      }
    }
  };

  const changeView = (next: ListView) => {
    if (next === view) return;
    setView(next);
    setPage(1);
  };

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortOrder(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortBy(field);
      setSortOrder('asc');
    }
    setPage(1);
  };

  const getStatusBadge = (status: string) => {
    const styles: Record<string, string> = {
      pending: 'bg-warning-100 text-warning-700',
      deposit_paid: 'bg-primary-100 text-primary-700',
      confirmed: 'bg-success-100 text-success-700',
      balance_paid: 'bg-success-100 text-success-700',
      completed: 'bg-warm-100 text-warm-600',
      cancelled: 'bg-danger-100 text-danger-700',
      refunded: 'bg-danger-100 text-danger-700',
    };
    const labels: Record<string, string> = {
      pending: 'Pending',
      deposit_paid: 'Deposit Paid',
      confirmed: 'Confirmed',
      balance_paid: 'Balance Paid',
      completed: 'Completed',
      cancelled: 'Cancelled',
      refunded: 'Refunded',
    };
    return (
      <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || 'bg-warm-100 text-warm-600'}`}>
        {labels[status] || status}
      </span>
    );
  };

  const formatDate = (date: string) => {
    // Append noon time to date-only strings to prevent timezone shift
    // (e.g., '2026-09-05' parsed as UTC midnight shows as Sep 4 in US timezones)
    const dateStr = date.includes('T') ? date : date + 'T12:00:00';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  const handleExportCSV = async () => {
    try {
      // Export must equal the view, so every filter loadBookings sends goes out here too.
      const exportFilters = {
        status: statusFilter !== 'all' ? statusFilter : undefined,
        show_id: showFilter || undefined,
        tour_stop_id: tourStopFilter || undefined,
        search: debouncedSearch || undefined,
        start_date: startDate || undefined,
        end_date: endDate || undefined,
        faculty_assignment: facultyFilter !== 'all' ? facultyFilter : undefined,
        created_start: createdStart || undefined,
        created_end: createdEnd || undefined,
        sort_by: sortBy,
        sort_order: sortOrder,
      };

      let csvText = '';
      const download = () => {
        const blob = new Blob([csvText], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = `bookings-${new Date().toISOString().split('T')[0]}.csv`;
        a.click();
        URL.revokeObjectURL(url);
      };

      if (view === 'classes') {
        // One line per class. Walk 500-row pages until a page comes back empty or we hold
        // `total` rows; the page cap (50 x 500) is only a runaway guard.
        const allRows: AdminBookingClassRow[] = [];
        let rowsTotal = 0;
        let rowsTruncated = false;
        let rowsSearchIncomplete = false;
        for (let exportPage = 1; exportPage <= EXPORT_MAX_PAGES; exportPage++) {
          const { rows, total: pageTotal, search_truncated } = await getAdminBookingClassRows({
            ...exportFilters,
            page: exportPage,
            limit: CLASS_EXPORT_PAGE_SIZE,
          });
          rowsTotal = pageTotal;
          allRows.push(...rows);
          if (search_truncated) rowsSearchIncomplete = true;
          if (rows.length === 0 || allRows.length >= pageTotal) break;
          if (exportPage === EXPORT_MAX_PAGES) rowsTruncated = true;
        }

        csvText = [
          ['Booking', 'Studio', 'Show', 'City', 'Class Date', 'Start Time', 'Faculty', 'Faculty Status', 'Class Price', 'Booking Total', 'Booking Status', 'Created'].join(','),
          ...allRows.map((r) => {
            const city = r.class.tour_stop?.city;
            return [
              csvCell(r.booking.booking_number || r.booking.id),
              csvCell(r.booking.studio?.name || ''),
              csvCell(r.class.tour_stop?.show?.name || ''),
              csvCell(city ? `${city.name}, ${city.state}` : ''),
              r.class.class_date,
              csvCell(r.class.start_time ? formatTimeDisplay(r.class.start_time) : ''),
              csvCell(facultyName(r)),
              classFacultyState(r)?.label || '',
              (r.class.total_cents / 100).toFixed(2),
              (r.booking.total_cents / 100).toFixed(2),
              r.booking.status,
              r.booking.created_at,
            ].join(',');
          }),
        ].join('\n');
        download();

        const rowNotices: string[] = [];
        if (rowsTruncated) {
          rowNotices.push(`only the first ${allRows.length} of ${rowsTotal} classes are in the file`);
        }
        if (rowsSearchIncomplete) {
          rowNotices.push('some matching bookings are missing because your search matches too many studios to check them all');
        }
        if (rowNotices.length > 0) {
          setError(`Export incomplete — ${rowNotices.join('; ')}. Try a more specific search or narrower filters, then export again.`);
        }
        return;
      }

      // The API caps per_page at 100, so walk pages until we have the whole result set.
      const allBookings: Booking[] = [];
      let exportTotal = 0;
      let truncated = false;
      let searchIncomplete = false;
      for (let exportPage = 1; exportPage <= EXPORT_MAX_PAGES; exportPage++) {
        const { bookings: pageBookings, total: pageTotal, search_truncated } = await getBookings({
          ...exportFilters,
          page: exportPage,
          limit: EXPORT_PAGE_SIZE,
        });
        exportTotal = pageTotal;
        allBookings.push(...pageBookings);
        if (search_truncated) searchIncomplete = true;
        if (pageBookings.length === 0 || allBookings.length >= pageTotal) break;
        if (exportPage === EXPORT_MAX_PAGES) truncated = true;
      }

      csvText = [
        ['ID', 'Studio', 'Show', 'Classes', 'Status', 'Total', 'Created'].join(','),
        ...allBookings.map((b) =>
          [
            b.id,
            csvCell(b.studio?.name || ''),
            csvCell(b.classes?.[0]?.tour_stop?.show?.name || ''),
            b.classes?.length || 0,
            b.status,
            ((b.total_cents || 0) / 100).toFixed(2),
            b.created_at,
          ].join(',')
        ),
      ].join('\n');

      download();

      const notices: string[] = [];
      if (truncated) {
        notices.push(`only the first ${allBookings.length} of ${exportTotal} bookings are in the file`);
      }
      if (searchIncomplete) {
        notices.push('some matching bookings are missing because your search matches too many studios to check them all');
      }
      if (notices.length > 0) {
        setError(`Export incomplete — ${notices.join('; ')}. Try a more specific search or narrower filters, then export again.`);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to export');
    }
  };

  const totalPages = Math.ceil(total / LIMIT);

  // 'active' is the default view, so it counts as the neutral baseline (not a filter).
  const hasActiveFilters = statusFilter !== 'active' || showFilter || searchQuery || startDate || endDate || facultyFilter !== 'all' || createdStart || createdEnd;
  const activeCount = [statusFilter !== 'active', showFilter, searchQuery, startDate || endDate, facultyFilter !== 'all', createdStart || createdEnd].filter(Boolean).length;
  const clearAll = () => { setStatusFilter('active'); setShowFilter(''); setSearchQuery(''); setStartDate(''); setEndDate(''); setFacultyFilter('all'); setCreatedStart(''); setCreatedEnd(''); setPage(1); };

  if (isLoading && !hasLoaded) {
    return (
      <div>
        <div className="flex items-center justify-between mb-8">
          <div className="h-10 bg-warm-200 rounded w-1/4 animate-pulse" />
          <div className="h-10 bg-warm-200 rounded w-32 animate-pulse" />
        </div>
        <div className="bg-white rounded-xl shadow-sm">
          {[1, 2, 3, 4, 5].map((i) => (
            <div key={i} className="p-6 border-b border-warm-100 animate-pulse">
              <div className="h-6 bg-warm-100 rounded w-1/3 mb-2" />
              <div className="h-4 bg-warm-100 rounded w-1/2" />
            </div>
          ))}
        </div>
      </div>
    );
  }

  return (
    <div>
      {/* Tour stop filter banner */}
      {tourStopFilter && (
        <div className="mb-6 p-3 bg-primary-50 border border-primary-200 rounded-lg flex items-center justify-between">
          <span className="text-sm text-primary-700">
            Showing bookings for a specific tour stop
          </span>
          <Link
            to="/admin/bookings"
            className="text-sm text-primary-600 hover:text-primary-700 font-medium"
          >
            Show all bookings
          </Link>
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-8">
        <div>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-warm-900">Bookings</h1>
          <p className="text-warm-600 mt-1 hidden sm:block">
            Manage all studio bookings and payments.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={handleExportCSV}
            className="px-4 py-2.5 min-h-[44px] border border-warm-300 text-warm-700 rounded-lg hover:bg-warm-50 transition-colors"
          >
            <svg className="w-5 h-5 mr-2 inline" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
            </svg>
            <span className="hidden sm:inline">Export </span>CSV
          </button>
          <Link to="/admin/bookings/new" className="btn-primary min-h-[44px] flex items-center">
            <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
            </svg>
            <span className="hidden sm:inline">Manual </span>Booking
          </Link>
        </div>
      </div>

      {/* View toggle */}
      <div className="mb-4 inline-flex rounded-lg border border-warm-200 bg-white p-0.5" role="group" aria-label="List view">
        {([
          ['classes', 'One row per class'],
          ['bookings', 'Group by booking'],
        ] as const).map(([value, label]) => (
          <button
            key={value}
            type="button"
            aria-pressed={view === value}
            onClick={() => changeView(value)}
            className={`px-3 py-2 min-h-[44px] text-sm font-medium rounded-md transition-colors ${
              view === value ? 'bg-primary-600 text-white' : 'text-warm-700 hover:bg-warm-50'
            }`}
          >
            {label}
          </button>
        ))}
      </div>

      {/* Filters */}
      {(() => {
        const inputCls = "px-3 py-2 text-sm border border-warm-200 rounded-lg bg-white focus:ring-2 focus:ring-primary-500 focus:border-transparent outline-none";
        return (
          <div className="bg-white rounded-xl shadow-sm mb-6 overflow-hidden">
            {/* Search row */}
            <div className="p-3 border-b border-warm-100">
              <div className="relative">
                <svg className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-warm-400 pointer-events-none" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
                <input
                  type="text"
                  value={searchQuery}
                  onChange={(e) => { setSearchQuery(e.target.value); setPage(1); }}
                  placeholder="Search studio name or booking ID..."
                  className="w-full pl-9 pr-9 py-2.5 text-sm border-0 outline-none text-warm-900 placeholder-warm-400 bg-transparent"
                />
                {isLoading && hasLoaded && (
                  <svg className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Searching">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
                  </svg>
                )}
              </div>
            </div>

            {/* Primary filters row */}
            <div className="px-3 py-2.5 flex flex-wrap items-center gap-2 border-b border-warm-100">
              <select
                value={statusFilter}
                onChange={(e) => { setStatusFilter(e.target.value as BookingStatus); setPage(1); }}
                className={`${inputCls} ${statusFilter !== 'active' ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium' : ''}`}
              >
                <option value="active">Active</option>
                <option value="all">All Status</option>
                <option value="pending">Pending</option>
                <option value="deposit_paid">Deposit Paid</option>
                <option value="confirmed">Confirmed</option>
                <option value="balance_paid">Balance Paid</option>
                <option value="completed">Completed</option>
                <option value="cancelled">Cancelled</option>
                <option value="refunded">Refunded</option>
              </select>

              <select
                value={showFilter}
                onChange={(e) => { setShowFilter(e.target.value); setPage(1); }}
                className={`${inputCls} ${showFilter ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium' : ''}`}
              >
                <option value="">All Shows</option>
                {shows.map((show) => (
                  <option key={show.id} value={show.id}>{show.name}</option>
                ))}
              </select>

              <select
                value={facultyFilter}
                onChange={(e) => { setFacultyFilter(e.target.value as 'all' | 'assigned' | 'unassigned'); setPage(1); }}
                className={`${inputCls} ${facultyFilter !== 'all' ? 'border-primary-400 bg-primary-50 text-primary-700 font-medium' : ''}`}
              >
                <option value="all">All Faculty</option>
                <option value="assigned">Assigned</option>
                <option value="unassigned">Unassigned</option>
              </select>

              {hasActiveFilters && (
                <button
                  onClick={clearAll}
                  className="ml-auto text-xs text-warm-500 hover:text-danger-600 font-medium transition-colors flex items-center gap-1"
                >
                  <svg className="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                  Clear {activeCount > 1 ? `${activeCount} filters` : 'filter'}
                </button>
              )}
            </div>

            {/* Date ranges row */}
            <div className="px-3 py-2.5 bg-warm-50 flex flex-wrap items-center gap-x-5 gap-y-2">
              {/* Class Date */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-warm-500 uppercase tracking-wider whitespace-nowrap">Class Date</span>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => { setStartDate(e.target.value); setPage(1); }}
                  className={`${inputCls} text-xs py-1.5 ${startDate ? 'border-primary-400 bg-primary-50 text-primary-700' : ''}`}
                />
                <span className="text-warm-300 text-sm">→</span>
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => { setEndDate(e.target.value); setPage(1); }}
                  className={`${inputCls} text-xs py-1.5 ${endDate ? 'border-primary-400 bg-primary-50 text-primary-700' : ''}`}
                />
              </div>

              <div className="w-px h-5 bg-warm-200 hidden sm:block" />

              {/* Booking Created */}
              <div className="flex items-center gap-2">
                <span className="text-xs font-semibold text-warm-500 uppercase tracking-wider whitespace-nowrap">Booked</span>
                <input
                  type="date"
                  value={createdStart}
                  onChange={(e) => { setCreatedStart(e.target.value); setPage(1); }}
                  className={`${inputCls} text-xs py-1.5 ${createdStart ? 'border-primary-400 bg-primary-50 text-primary-700' : ''}`}
                />
                <span className="text-warm-300 text-sm">→</span>
                <input
                  type="date"
                  value={createdEnd}
                  onChange={(e) => { setCreatedEnd(e.target.value); setPage(1); }}
                  className={`${inputCls} text-xs py-1.5 ${createdEnd ? 'border-primary-400 bg-primary-50 text-primary-700' : ''}`}
                />
              </div>
            </div>
          </div>
        );
      })()}

      {error && (
        <div className="mb-6 p-4 bg-danger-50 border border-danger-200 rounded-lg text-danger-700">
          {error}
        </div>
      )}

      {/* During refetch the previous rows stay mounted, dimmed under a spinner overlay,
          so a search or filter change never blanks the list and repaints (same pattern
          as InquiriesListPage). The partial-results banner sits inside so it dims too
          rather than asserting a stale claim about results still being fetched. */}
      <div className="relative">
        {isLoading && hasLoaded && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/50 rounded-xl">
            <svg className="w-8 h-8 text-primary-500 animate-spin" viewBox="0 0 24 24" fill="none" aria-label="Loading bookings">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4a4 4 0 00-4 4H4z" />
            </svg>
          </div>
        )}

      {searchTruncated && (
        <div className="mb-6 p-4 bg-warning-50 border border-warning-200 rounded-lg text-warning-700">
          Some matching bookings may be missing — your search matches too many studios to check them all. Keep typing to narrow it.
        </div>
      )}

      {/* Bookings List */}
      {(view === 'classes' ? classRows.length : bookings.length) === 0 && !isLoading && loadedView === view ? (
        <div className="bg-white rounded-xl shadow-sm p-12 text-center">
          <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-warm-100 flex items-center justify-center">
            <svg className="w-8 h-8 text-warm-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <h3 className="text-lg font-semibold text-warm-900 mb-2">
            {view === 'classes' ? 'No classes found' : 'No bookings found'}
          </h3>
          <p className="text-warm-600 mb-6">
            {hasActiveFilters || searchQuery
              ? 'Try adjusting your filters to see more results.'
              : 'Bookings will appear here once studios start booking.'}
          </p>
        </div>
      ) : (
        <>
          {view === 'classes' && (
            <>
              {/* Desktop table — one row per class */}
              <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
                <table className="w-full">
                  <thead className="bg-warm-50 border-b border-warm-100">
                    <tr>
                      <SortableHeader label="Booking" field="booking_number" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <SortableHeader label="Studio" field="studio_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <SortableHeader label="Class" field="class_date" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <SortableHeader label="Faculty" field="faculty_name" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <SortableHeader label="Total" field="total_cents" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <SortableHeader label="Status" field="status" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <SortableHeader label="Created" field="created_at" currentSort={sortBy} currentOrder={sortOrder} onSort={handleSort} />
                      <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-warm-100">
                    {classRows.map((row) => {
                      const fac = classFacultyState(row);
                      const city = row.class.tour_stop?.city;
                      const detailsPath = `/admin/bookings/${row.booking.id}`;
                      return (
                        <tr
                          key={row.class.id}
                          className={`hover:bg-warm-50 transition-colors ${fac?.key === 'unassigned' ? 'bg-danger-50/30' : ''}`}
                        >
                          <td className="px-6 py-4">
                            <Link to={detailsPath} className="font-mono text-sm text-primary-600 hover:text-primary-700">
                              #{row.booking.booking_number || row.booking.id.slice(0, 8)}
                            </Link>
                            {row.booking.class_count > 1 && (
                              <span className="ml-2 inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-warm-100 text-warm-600">
                                Class {row.booking.class_index} of {row.booking.class_count}
                              </span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <p className="font-medium text-warm-900">{row.booking.studio?.name || 'Unknown Studio'}</p>
                            <p className="text-sm text-warm-500">{row.booking.studio?.primary_contact?.email}</p>
                          </td>
                          <td className="px-6 py-4">
                            <p className="text-sm text-warm-900">{row.class.tour_stop?.show?.name}</p>
                            <p className="text-sm text-warm-500">
                              {formatDate(row.class.class_date)}
                              {row.class.start_time && ` · ${formatTimeDisplay(row.class.start_time)}`}
                            </p>
                            {city && <p className="text-xs text-warm-500">{city.name}, {city.state}</p>}
                          </td>
                          <td className="px-6 py-4">
                            {fac ? (
                              <div className="space-y-1">
                                {row.class.faculty && <p className="text-sm text-warm-900">{facultyName(row)}</p>}
                                <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${FACULTY_CHIP_STYLES[fac.key]}`}>
                                  {fac.label}
                                </span>
                              </div>
                            ) : (
                              <span className="text-warm-400 text-sm">--</span>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <span className="font-semibold text-warm-900">{formatCurrency(row.class.total_cents)}</span>
                            {row.booking.class_count > 1 && (
                              <p className="text-xs text-warm-500">of {formatCurrency(row.booking.total_cents)} booking</p>
                            )}
                          </td>
                          <td className="px-6 py-4">
                            <div className="space-y-1">
                              {getStatusBadge(row.booking.status)}
                              {(row.class.status === 'completed' || row.class.status === 'cancelled') &&
                                row.class.status !== row.booking.status && (
                                  <div>
                                    <span className={`inline-block whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium ${row.class.status === 'cancelled' ? 'bg-danger-100 text-danger-700' : 'bg-warm-100 text-warm-600'}`}>
                                      Class {row.class.status === 'cancelled' ? 'cancelled' : 'completed'}
                                    </span>
                                  </div>
                                )}
                            </div>
                          </td>
                          <td className="px-6 py-4">
                            <span className="text-warm-600 text-sm">{formatDate(row.booking.created_at)}</span>
                          </td>
                          <td className="px-6 py-4 text-right">
                            <Link
                              to={detailsPath}
                              className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                            >
                              View Details
                            </Link>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>

              {/* Mobile cards — one per class */}
              <div className="md:hidden space-y-3">
                {classRows.map((row) => {
                  const fac = classFacultyState(row);
                  const city = row.class.tour_stop?.city;
                  return (
                    <Link
                      to={`/admin/bookings/${row.booking.id}`}
                      key={row.class.id}
                      className={`block rounded-xl border p-4 active:scale-[0.98] transition-transform hover:border-warm-300 ${fac?.key === 'unassigned' ? 'bg-danger-50/30 border-danger-200' : 'bg-white border-warm-200'}`}
                    >
                      <div className="flex items-center justify-between mb-2">
                        {getStatusBadge(row.booking.status)}
                        <span className="font-semibold text-warm-900">{formatCurrency(row.class.total_cents)}</span>
                      </div>
                      <p className="font-medium text-warm-900">{row.booking.studio?.name || 'Unknown Studio'}</p>
                      <p className="text-sm text-warm-600">{row.class.tour_stop?.show?.name}</p>
                      <p className="text-xs text-warm-500 mt-1">
                        #{row.booking.booking_number || row.booking.id.slice(0, 8)}
                        {row.booking.class_count > 1 && ` · Class ${row.booking.class_index} of ${row.booking.class_count}`}
                      </p>
                      <div className="flex items-center justify-between mt-2 text-xs text-warm-500">
                        <span>
                          {formatDate(row.class.class_date)}
                          {row.class.start_time && ` · ${formatTimeDisplay(row.class.start_time)}`}
                          {city && ` · ${city.name}`}
                        </span>
                        {fac && (
                          <span className={`whitespace-nowrap px-2 py-0.5 rounded-full font-medium ${FACULTY_CHIP_STYLES[fac.key]}`}>
                            {fac.label}
                          </span>
                        )}
                      </div>
                    </Link>
                  );
                })}
              </div>
            </>
          )}

          {view === 'bookings' && (
            <>
          {/* Desktop table */}
          <div className="hidden md:block bg-white rounded-xl shadow-sm overflow-hidden">
            <table className="w-full">
              <thead className="bg-warm-50 border-b border-warm-100">
                <tr>
                  <SortableHeader
                    label="Booking"
                    field="booking_number"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Studio"
                    field="studio_name"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Classes"
                    field="class_date"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Faculty"
                    field="faculty_name"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Total"
                    field="total_cents"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Status"
                    field="status"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <SortableHeader
                    label="Created"
                    field="created_at"
                    currentSort={sortBy}
                    currentOrder={sortOrder}
                    onSort={handleSort}
                  />
                  <th className="text-right px-6 py-4 text-sm font-semibold text-warm-700">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="divide-y divide-warm-100">
                {bookings.map((booking) => {
                  const needsFaculty = booking.status !== 'cancelled' &&
                    booking.classes?.some(c => !c.faculty_id);
                  return (
                  <tr key={booking.id} className={`hover:bg-warm-50 transition-colors ${needsFaculty ? 'bg-danger-50/30' : ''}`}>
                    <td className="px-6 py-4">
                      <Link
                        to={`/admin/bookings/${booking.id}`}
                        className="font-mono text-sm text-primary-600 hover:text-primary-700"
                      >
                        #{booking.booking_number || booking.id.slice(0, 8)}
                      </Link>
                    </td>
                    <td className="px-6 py-4">
                      <div>
                        <p className="font-medium text-warm-900">
                          {booking.studio?.name || 'Unknown Studio'}
                        </p>
                        <p className="text-sm text-warm-500">
                          {booking.studio?.primary_contact?.email}
                        </p>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        // Earliest class first, so the two shown are the next ones up.
                        const sorted = [...(booking.classes || [])].sort((x, y) =>
                          (x.scheduled_date || '').localeCompare(y.scheduled_date || '')
                        );
                        const expanded = expandedBookingIds.has(booking.id);
                        const shown = expanded ? sorted : sorted.slice(0, 2);
                        return (
                          <div className="space-y-1">
                            {shown.map((cls) => (
                              <div key={cls.id} className="text-sm">
                                <span className="text-warm-900">
                                  {cls.tour_stop?.show?.name}
                                </span>
                                <span className="text-warm-500 ml-2">
                                  {formatDate(cls.scheduled_date)}
                                </span>
                              </div>
                            ))}
                            {sorted.length > 2 && (
                              <button
                                type="button"
                                aria-expanded={expanded}
                                onClick={() =>
                                  setExpandedBookingIds((prev) => {
                                    const next = new Set(prev);
                                    if (next.has(booking.id)) next.delete(booking.id);
                                    else next.add(booking.id);
                                    return next;
                                  })
                                }
                                className="text-xs text-primary-600 hover:text-primary-700 hover:underline"
                              >
                                {expanded ? 'Show less' : `+${sorted.length - 2} more`}
                              </button>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      {(() => {
                        const classes = booking.classes || [];
                        const totalClasses = classes.length;
                        const assignedClasses = classes.filter(c => c.faculty_id).length;
                        const isCancelled = booking.status === 'cancelled';

                        if (isCancelled || totalClasses === 0) {
                          return <span className="text-warm-400 text-sm">--</span>;
                        }
                        if (assignedClasses === totalClasses) {
                          const allConfirmed = classes.every(
                            c => c.faculty_id && c.faculty_assignment_status === 'accepted'
                          );
                          if (allConfirmed) {
                            return (
                              <span className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-success-100 text-success-700">
                                <span className="text-[10px]">&#x2713;</span>
                                Confirmed
                              </span>
                            );
                          }
                          return (
                            <span className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-warning-100 text-warning-700">
                              Assigned
                            </span>
                          );
                        }
                        return (
                          <span className="inline-flex items-center gap-1 whitespace-nowrap px-2 py-0.5 rounded-full text-xs font-medium bg-danger-100 text-danger-700">
                            <span className="text-[10px]">!</span>
                            {assignedClasses}/{totalClasses} assigned
                          </span>
                        );
                      })()}
                    </td>
                    <td className="px-6 py-4">
                      <span className="font-semibold text-warm-900">
                        {formatCurrency(booking.total_cents || 0)}
                      </span>
                    </td>
                    <td className="px-6 py-4">
                      {getStatusBadge(booking.status)}
                    </td>
                    <td className="px-6 py-4">
                      <span className="text-warm-600 text-sm">
                        {formatDate(booking.created_at)}
                      </span>
                    </td>
                    <td className="px-6 py-4 text-right">
                      <Link
                        to={`/admin/bookings/${booking.id}`}
                        className="px-3 py-1.5 text-sm text-primary-600 hover:text-primary-700 hover:bg-primary-50 rounded-lg transition-colors"
                      >
                        View Details
                      </Link>
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Mobile cards */}
          <div className="md:hidden space-y-3">
            {bookings.map((booking) => (
              <Link
                to={`/admin/bookings/${booking.id}`}
                key={booking.id}
                className="block bg-white rounded-xl border border-warm-200 p-4 active:scale-[0.98] transition-transform hover:border-warm-300"
              >
                <div className="flex items-center justify-between mb-2">
                  {getStatusBadge(booking.status)}
                  <span className="font-semibold text-warm-900">
                    {formatCurrency(booking.total_cents || 0)}
                  </span>
                </div>
                <p className="font-medium text-warm-900">
                  {booking.studio?.name || 'Unknown Studio'}
                </p>
                {booking.classes?.slice(0, 1).map((cls) => (
                  <p key={cls.id} className="text-sm text-warm-600">
                    {cls.tour_stop?.show?.name}
                  </p>
                ))}
                <div className="flex items-center justify-between mt-2 text-xs text-warm-500">
                  <span>
                    {booking.classes?.[0] && formatDate(booking.classes[0].scheduled_date)}
                    {booking.classes?.[0]?.tour_stop?.city && ` · ${booking.classes[0].tour_stop.city.name}`}
                  </span>
                  {(() => {
                    const classes = booking.classes || [];
                    const assignedClasses = classes.filter(c => c.faculty_id).length;
                    if (booking.status !== 'cancelled' && classes.length > 0 && assignedClasses < classes.length) {
                      return (
                        <span className="text-danger-600 font-medium">
                          {assignedClasses}/{classes.length} faculty
                        </span>
                      );
                    }
                    return null;
                  })()}
                </div>
              </Link>
            ))}
          </div>
            </>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <Pagination
              page={page}
              total={total}
              limit={LIMIT}
              itemLabel={view === 'classes' ? 'classes' : 'bookings'}
              onPageChange={setPage}
            />
          )}
        </>
      )}
      </div>
    </div>
  );
}
